/**
 * sanitize.ts — Input-Sanitize-Utilities für DB-Write und Notif-Body
 *
 * Verwendet bei Form-Submit und überall wo User-Eingaben in DB,
 * Mail-Body, Push-Notif oder andere persistente/transportierte Stellen
 * landen. Nicht zu verwechseln mit escape.ts — das ist für HTML-Rendering.
 *
 * Pattern entstand aus dem Audit-Sprint vom 28-29.04.2026 (Bugs 50, 53,
 * 54, 62, 63, 65 — User-Input ohne Längen-Limits/Validation in mehreren
 * Stellen). Statt überall im Code eigene Inline-Regex zu basteln, alles
 * hier zentral.
 *
 * ZIEL: Defense-in-Depth-Schicht zwischen Frontend-Form und DB-Write.
 * - Längen-Limits gegen versehentliches Mega-Paste oder gezielte Bloat-Angriffe
 * - Newline/Tab-Strip für einzeilige Felder (verhindert Layout-Brüche
 *   in Mail-Templates und potenzielle SMTP-Header-Injection-Vektoren)
 * - Trim, damit der User keine versehentlichen Whitespace-Probleme hat
 *
 * ABGRENZUNG:
 * - sanitizeOneLine: für einzeilige Stammdaten (firma, plz, telefon, ...)
 * - sanitizeMultiLine: für Free-Text mit Absätzen (notizen, transportAnweisungen)
 * - sanitizeNumber: für Zahlen-Inputs mit Range-Schutz
 * - sanitizeIban / sanitizeBic: für Bank-Stammdaten mit Format-Hint
 *
 * @module lib/sanitize
 */

// ════════════════════════════════════════════════════════════════════
// Strings
// ════════════════════════════════════════════════════════════════════

/**
 * Strippt Newlines, Tabs, Control-Chars; trimmt; cap auf maxLen.
 *
 * Für einzeilige Felder (Stammdaten, Adressen, Namen). Die Steuerzeichen-
 * Entfernung schützt vor:
 * - Layout-Brüchen in Plaintext-Mail-Bodies
 * - SMTP-Header-Injection (CR/LF im Subject)
 * - Display-Bugs in Listen/Tabellen
 *
 * @param input Beliebiger User-Input (auch null/undefined toleriert)
 * @param maxLen Maximale Länge nach Strip+Trim (Default 200)
 * @returns Bereinigter String, leer wenn Input falsy
 */
export function sanitizeOneLine(
  input: string | null | undefined,
  maxLen = 200,
): string {
  if (!input) return '';
  // Steuerzeichen + Newlines + Tabs durch Spaces ersetzen
  // (nicht entfernen — sonst verschmelzen Worte ungewollt).
  // Range \x00-\x1F deckt alle ASCII-Steuerzeichen ab (CR, LF, TAB, NULL, ...)
  // \x7F ist DEL.
  // eslint-disable-next-line no-control-regex
  const cleaned = String(input).replace(/[\r\n\t\x00-\x1F\x7F]/g, ' ').trim();
  // Mehrere Spaces zu einem (kosmetisch, hilft bei Layout)
  const collapsed = cleaned.replace(/  +/g, ' ');
  return collapsed.length > maxLen ? collapsed.slice(0, maxLen) : collapsed;
}

/**
 * Wie sanitizeOneLine, aber erlaubt Newlines (für Free-Text-Felder).
 *
 * Nutzt Tabs als Spaces (Tabs sind in Free-Text fast nie sinnvoll).
 * Strippt nur "echte" Steuerzeichen (\x00-\x08, \x0B-\x1F, \x7F),
 * lässt aber \x09 (Tab → Space), \x0A (LF), \x0D (CR) durch.
 *
 * @param input Beliebiger User-Input (auch null/undefined toleriert)
 * @param maxLen Maximale Länge (Default 50000 ≈ 10 A4-Seiten)
 * @returns Bereinigter String mit erhaltenen Newlines
 */
export function sanitizeMultiLine(
  input: string | null | undefined,
  maxLen = 50000,
): string {
  if (!input) return '';
  // Tabs → Space, andere Control-Chars (außer LF/CR) entfernen.
  // eslint-disable-next-line no-control-regex
  const cleaned = String(input)
    .replace(/\t/g, ' ')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  // CRLF → LF normalisieren (Windows-Eingaben)
  const normalized = cleaned.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Trim außen (entfernt Whitespace inkl. Newlines), Leerzeilen innen erhalten
  const trimmed = normalized.trim();
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
}

// ════════════════════════════════════════════════════════════════════
// Zahlen
// ════════════════════════════════════════════════════════════════════

/** Input für `sanitizeNumber`. */
export interface SanitizeNumberOptions {
  /** Fallback wenn Parse fehlschlägt (NaN). Default: 0. */
  fallback?: number;
  /** Untergrenze (inklusive). Werte darunter werden auf min geklemmt. Default: 0. */
  min?: number;
  /** Obergrenze (inklusive). Werte darüber werden auf fallback gesetzt
   *  (defensiv, weil "zu groß" oft Tippfehler oder 1e10 ist). Default: 1_000_000. */
  max?: number;
}

/**
 * Robustes Number-Parsing mit Range-Schutz.
 *
 * Behandelt typische Frontend-Stolperfallen:
 * - "abc" → fallback (statt NaN || 0 = 0, was bei Preisen tragisch ist)
 * - "-50" → min (verhindert versehentliche Negativ-Preise)
 * - "1e10" / sehr groß → fallback (Copy-Paste-Unfälle)
 * - "" / null → fallback
 * - "12,50" → 12.50 (Komma als Dezimaltrenner toleriert)
 * - "  3.14  " → 3.14 (Whitespace toleriert)
 *
 * @param input Roh-Input (typischerweise input.value)
 * @param options Min/Max/Fallback. Default: 0..1_000_000, fallback 0.
 * @returns Sicherer Number-Wert
 */
export function sanitizeNumber(
  input: string | number | null | undefined,
  options: SanitizeNumberOptions = {},
): number {
  const { fallback = 0, min = 0, max = 1_000_000 } = options;
  if (input === null || input === undefined || input === '') return fallback;
  // String-Input: Komma → Punkt für deutsche Eingabe
  const normalized =
    typeof input === 'string'
      ? input.trim().replace(',', '.')
      : input;
  const parsed = typeof normalized === 'number' ? normalized : parseFloat(normalized);
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) return fallback;
  if (parsed < min) return min;
  if (parsed > max) return fallback;
  return parsed;
}

// ════════════════════════════════════════════════════════════════════
// IBAN / BIC
// ════════════════════════════════════════════════════════════════════

/**
 * IBAN-Sanitize: Whitespace-Strip, Uppercase. Liefert leer bei zu langem Input.
 *
 * Macht KEINE Mod-97-Validierung (Checksum) — das wäre ein eigener
 * Sprint und User können auch ohne perfekte IBAN ihr Profil speichern
 * (sie sehen nur ihre Rechnung später unschön). Wir blockieren nur
 * offensichtlich kaputte Inputs (zu lang, mit Sonderzeichen).
 *
 * IBAN-Maximallänge: 34 Zeichen (ISO 13616). Deutsche IBAN: 22 Zeichen.
 * Bei mehr als 34 Zeichen (z.B. Doppel-Paste, Müll) wird leer zurückgegeben,
 * damit kein verstümmelter Müll-String die nachgelagerte Mod-97-Prüfung
 * zufällig passieren kann.
 *
 * @param input Roh-IBAN (z.B. "DE89 3704 0044 0532 0130 00")
 * @returns "DE89370400440532013000" oder leer
 */
export function sanitizeIban(input: string | null | undefined): string {
  if (!input) return '';
  // Alle Whitespaces raus, Uppercase
  const stripped = String(input).replace(/\s+/g, '').toUpperCase();
  // Nur A-Z und 0-9 erlaubt (kein +, -, ., etc.)
  const filtered = stripped.replace(/[^A-Z0-9]/g, '');
  // Übermäßig lange Eingaben (Doppel-Paste, Müll) als leer signalisieren —
  // ein stilles slice(0, 34) würde einen 34-Zeichen-Müll-String erzeugen,
  // der zufällig die Mod-97-Prüfung umgehen könnte. Defense-in-Depth.
  if (filtered.length > 34) return '';
  return filtered;
}

/**
 * BIC-Sanitize: Whitespace-Strip, Uppercase, Längen-Cap auf 11.
 *
 * BIC-Format (ISO 9362): 8 oder 11 Zeichen, A-Z + 0-9.
 *
 * @param input Roh-BIC (z.B. "cobadeff" oder "COBADEFFXXX")
 * @returns "COBADEFF" oder "COBADEFFXXX" oder leer
 */
export function sanitizeBic(input: string | null | undefined): string {
  if (!input) return '';
  const stripped = String(input).replace(/\s+/g, '').toUpperCase();
  const filtered = stripped.replace(/[^A-Z0-9]/g, '');
  // Analog zu sanitizeIban: lieber leer als verstümmelt.
  if (filtered.length > 11) return '';
  return filtered;
}

// ════════════════════════════════════════════════════════════════════
// IBAN-Validierung (Mod-97, ISO 13616)
// ════════════════════════════════════════════════════════════════════

/**
 * Erwartete IBAN-Länge pro Land. Quelle: SWIFT IBAN Registry.
 * Auswahl: alle EU/EWR-Länder + UK + CH (DRPX-Fokus). Wenn das Land
 * nicht in der Map ist, fällt isValidIban auf eine generische Längen-
 * Range (15-34) zurück und prüft nur die Mod-97-Checksum.
 */
const IBAN_LENGTHS: Record<string, number> = {
  AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18,
  EE: 20, ES: 24, FI: 18, FR: 27, GB: 22, GR: 27, HR: 21, HU: 28,
  IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MT: 31,
  NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24,
  TR: 26,
};

/**
 * Prüft IBAN-Syntax via Mod-97-Algorithmus (ISO 13616).
 *
 * Was geprüft wird:
 *  1. Format: 2 Buchstaben + 2 Ziffern + Rest alphanumerisch
 *  2. Länge: passt zur Länderkennung (DE=22, AT=20, etc.)
 *  3. Checksum: rearranged-IBAN als Big-Number mod 97 = 1
 *
 * Was NICHT geprüft wird:
 *  - Existenz des Kontos (geht nur via Bank-API)
 *  - Existenz der Bankleitzahl (BLZ-Verzeichnis)
 *  - Aktivität / Sperrungen
 *
 * Fängt typische Tippfehler ab (Zahlendreher, vergessene Stelle,
 * verlesene Ziffer). Wahrscheinlichkeit eines False-Positive: ~1:97.
 *
 * Vor dem Aufruf: Input mit `sanitizeIban` cleanen (Whitespace + Case).
 * Diese Funktion erwartet bereits sanitisierten Input — sonst gibt sie
 * `false` für offensichtlich kaputte Eingaben zurück.
 *
 * Beispiel:
 *   isValidIban('DE89370400440532013000') → true
 *   isValidIban('DE89370400440532013001') → false (letzte Ziffer falsch)
 *   isValidIban('DE893704004405320130')   → false (zu kurz für DE)
 *
 * @param iban Sanitisierter IBAN-String (kein Whitespace, Uppercase)
 * @returns true wenn syntaktisch gültig, sonst false
 */
export function isValidIban(iban: string | null | undefined): boolean {
  if (!iban) return false;
  // Format-Check: 2 Buchstaben + 2 Ziffern + alphanumerisch
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban)) return false;
  // Längen-Check
  if (iban.length < 15 || iban.length > 34) return false;
  const country = iban.slice(0, 2);
  const expectedLen = IBAN_LENGTHS[country];
  if (expectedLen !== undefined && iban.length !== expectedLen) return false;

  // Mod-97 Algorithmus:
  // 1. Erste 4 Zeichen ans Ende verschieben
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  // 2. Buchstaben → Zahlen (A=10, B=11, ..., Z=35)
  const numeric = rearranged.replace(/[A-Z]/g, (ch) =>
    String(ch.charCodeAt(0) - 55),
  );
  // 3. Mod 97 stückweise rechnen (Number-Range reicht nicht für 30+ Stellen,
  //    BigInt wäre möglich aber unnötig — wir teilen den String in Stücke
  //    von max 9 Ziffern auf, halten den Rest, ziehen die nächsten Ziffern dran).
  let remainder = '';
  for (let i = 0; i < numeric.length; i++) {
    remainder = remainder + numeric[i];
    // 9 Ziffern passen sicher in JS Number (Number.MAX_SAFE_INTEGER hat 16)
    if (remainder.length >= 9) {
      remainder = String(parseInt(remainder, 10) % 97);
    }
  }
  return parseInt(remainder, 10) % 97 === 1;
}

